import { inject, Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateFn, CanDeactivateFn } from '@angular/router';

import { AuthenticationService } from '../services/AuthenticationService';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../shared/dialog/confirmation-dialog.component';
import { filter, map, Observable, of, take } from 'rxjs';
import { UnsavedChangesAware } from './unsaved-changes-aware';

@Injectable({ providedIn: 'root' })
export class PermissionsService{

    constructor(private router: Router, private authenticationService: AuthenticationService, private dialog: MatDialog) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.authenticationService.authorized$.pipe(
            filter(ready => ready),   // wait until init() has actually resolved
            take(1),
            map(() => {
                const currentUser = this.authenticationService.currentUserValue;
                if (currentUser) {
                    if (route.data.roles && route.data.roles.indexOf(currentUser.role) === -1) {
                        this.router.navigate(['/']);
                        return false;
                    }
                    if (route.data.anonymousOnly) {
                        this.router.navigate(['/']);
                        return false;
                    }
                    return true;
                } else {
                    if (route.data.anonymousOnly) {
                        return true;
                    } else {
                        this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
                        return false;
                    }
                }
            })
        );
    }

    canDeactivate(component: UnsavedChangesAware, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> {
        if(component.hasPendingUploads){
            const dialogConfig = new MatDialogConfig();
            dialogConfig.disableClose = true;
            const unsaved = component.isEditMode() && component.hasUnsavedChanges ? " Your unsaved changes will be lost as well." : "";
            dialogConfig.data={title: "Uploads running", message: "Photos are still uploading. Leaving now cancels the uploads that are not finished." + unsaved, buttonText: {ok: "Leave", cancel: "Stay"}};
            return this.dialog.open(ConfirmationDialogComponent, dialogConfig).afterClosed().pipe(
                map((confirmed: boolean) => !!confirmed)
                );
        }
        if(component.isEditMode() && component.hasUnsavedChanges){
            const dialogConfig = new MatDialogConfig();
            dialogConfig.disableClose = true;
            dialogConfig.autoFocus = true;
            let title="Warning";
            let message="You have unsaved changes. Press Cancel to go back and save these changes, or OK to lose these changes."
            dialogConfig.data={title: title, message: message, buttonText: {ok: "OK", cancel: "Cancel"}};
        
            //the property lock is released by PropertyMasterService.ngOnDestroy (with the client session) once the route is left
            return this.dialog.open(ConfirmationDialogComponent, dialogConfig).afterClosed().pipe(
                map((confirmed: boolean) => !!confirmed)
                );
        }
        else return of(true);
    }
}

export const AuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    return inject(PermissionsService).canActivate(next, state);
}

export const PendingChangesGuard: CanDeactivateFn<UnsavedChangesAware> = (component, currentRoute, currentState, nextState): Observable<boolean> => {
    return inject(PermissionsService).canDeactivate(component, currentRoute, currentState, nextState);
}